import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory, useParams } from "react-router-dom";
import { db, storage } from "../Config/firebase";
import Navbar from "../Components/Navbar/navbar";
import RelatorioExtracoesPrint from "./RelatorioExtracoesPrint";
import { compressImage } from "../../utils/compressImage";
import "./DetalhesExtracao.css";
import { consultaTenantAtual } from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

function formatarDataBR(dataISO) {
  if (!dataISO) return "-";
  const [ano, mes, dia] = dataISO.split("-");
  if (!ano || !mes || !dia) return dataISO;
  return `${dia}/${mes}/${ano}`;
}

function novoTestemunho(index, dataExtracao) {
  return {
    peca: String(index + 1),
    identificacao: "",
    dataExtracao: dataExtracao || "",
    posicaoFace: "",
    diametro: "",
    altura: "",
    cargaRupturaTf: "",
    forcaRupturaN: "",
    tensaoInicial: "",
    k1: "",
    k2: "",
    k3: "",
    k4: "-0,04",
    tensaoCorrigida: "",
    massa: "",
    massaEspecifica: "",
    frenteCarbonatacao: "-",
    observacao: "",
  };
}

const TABELA_K1 = [
  { relacao: 2.0, valor: 0 },
  { relacao: 1.88, valor: -0.01 },
  { relacao: 1.75, valor: -0.02 },
  { relacao: 1.63, valor: -0.03 },
  { relacao: 1.5, valor: -0.04 },
  { relacao: 1.42, valor: -0.05 },
  { relacao: 1.33, valor: -0.06 },
  { relacao: 1.25, valor: -0.07 },
  { relacao: 1.21, valor: -0.08 },
  { relacao: 1.18, valor: -0.09 },
  { relacao: 1.14, valor: -0.1 },
  { relacao: 1.11, valor: -0.11 },
  { relacao: 1.07, valor: -0.12 },
  { relacao: 1.04, valor: -0.13 },
  { relacao: 1.0, valor: -0.14 },
];

const TABELA_K2 = [
  { diametro: 50, valor: 0.12 },
  { diametro: 75, valor: 0.09 },
  { diametro: 100, valor: 0.06 },
  { diametro: 150, valor: 0.04 },
];

const EXPLICACOES_COEFICIENTES = {
  K1: "Correção da relação altura/diâmetro (h/d). Divida a altura pelo diâmetro do testemunho e consulte a tabela de k1; para valores intermediários, use interpolação linear. Quando h/d = 2, k1 = 0.",
  K2: "Correção do efeito do broqueamento em função do diâmetro. Consulte a tabela de k2 usando o diâmetro do testemunho: 50 mm = 0,12; 75 mm = 0,09; 100 mm = 0,06; e 150 mm ou mais = 0,04. Valores intermediários podem ser interpolados.",
  K3: "Correção da direção da extração em relação ao lançamento do concreto. Use k3 = 0,05 quando a extração for ortogonal ao sentido do lançamento e k3 = 0 quando ocorrer no mesmo sentido.",
  K4: "Correção da condição de umidade do testemunho. Use k4 = 0 para ruptura na condição saturada e k4 = -0,04 para ensaio do testemunho seco ao ar.",
};

function CabecalhoCoeficiente({ nome }) {
  return (
    <Tooltip title={EXPLICACOES_COEFICIENTES[nome]} arrow placement="top">
      <Box
        component="span"
        sx={{ cursor: "help", borderBottom: "1px dotted currentColor" }}
      >
        {nome}
      </Box>
    </Tooltip>
  );
}

const inputTabelaSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
  },
  "& .MuiInputBase-input": {
    textAlign: "center",
    p: "7px 4px",
  },
};

const cellCenterSx = {
  textAlign: "center",
  verticalAlign: "middle",
};

const inputCellSx = {
  ...cellCenterSx,
  px: "4px",
  py: "3px",
};

const OPCOES_CARBONATACAO = ["-", "Não evidenciado", "Evidenciado"];

function corCarbonatacao(valor) {
  if (valor === "-") return "#334155";
  return valor === "Evidenciado" ? "#e57373" : "#2e7d32";
}

function numero(valor) {
  if (valor === "" || valor === null || valor === undefined) return 0;
  return Number(String(valor).replace(",", "."));
}

function temSinalAusencia(...valores) {
  return valores.some((valor) => String(valor ?? "").trim() === "-");
}

function arredondar(valor, casas = 3) {
  if (!Number.isFinite(valor)) return "";
  return valor.toFixed(casas);
}

function coeficientesPreenchidos(...coeficientes) {
  return coeficientes.every(
    (coeficiente) => String(coeficiente ?? "").trim() !== ""
  );
}

function calcularK1(altura, diametro) {
  if (temSinalAusencia(altura, diametro)) return "-";

  const h = numero(altura);
  const d = numero(diametro);
  if (!h || !d) return "";

  const relacao = h / d;

  if (relacao >= TABELA_K1[0].relacao) return arredondar(TABELA_K1[0].valor, 3);
  if (relacao <= TABELA_K1[TABELA_K1.length - 1].relacao) {
    return arredondar(TABELA_K1[TABELA_K1.length - 1].valor, 3);
  }

  for (let i = 0; i < TABELA_K1.length - 1; i++) {
    const atual = TABELA_K1[i];
    const proximo = TABELA_K1[i + 1];

    if (relacao <= atual.relacao && relacao >= proximo.relacao) {
      const proporcao =
        (relacao - proximo.relacao) / (atual.relacao - proximo.relacao);
      const interpolado =
        proximo.valor + proporcao * (atual.valor - proximo.valor);

      return arredondar(interpolado, 3);
    }
  }

  return "";
}

function calcularK2(diametro) {
  if (temSinalAusencia(diametro)) return "-";

  const d = numero(diametro);
  if (!d) return "";

  const maisProximo = TABELA_K2.reduce((selecionado, item) => {
    const distanciaAtual = Math.abs(item.diametro - d);
    const distanciaSelecionada = Math.abs(selecionado.diametro - d);
    return distanciaAtual < distanciaSelecionada ? item : selecionado;
  }, TABELA_K2[0]);

  return arredondar(maisProximo.valor, 2);
}

function calcularTensoesDoTestemunho(item) {
  const carga = numero(item.cargaRupturaTf);
  const diametro = numero(item.diametro);
  const altura = numero(item.altura);
  const massa = numero(item.massa);

  const diametroArea = diametro ? Math.round(diametro) : 0;
  const area = diametroArea ? (3.1416 * Math.pow(diametroArea, 2)) / 4 : 0;
  const forcaRupturaN = temSinalAusencia(item.cargaRupturaTf)
    ? "-"
    : carga
      ? carga * 1000 * 9.80665
      : 0;
  const tensaoInicial = temSinalAusencia(item.cargaRupturaTf, item.diametro)
    ? "-"
    : forcaRupturaN && area
      ? forcaRupturaN / area
      : 0;
  const k1 = calcularK1(altura, diametro);
  const k2 = calcularK2(diametro);
  const temTodosCoeficientes = coeficientesPreenchidos(k1, k2, item.k3, item.k4);
  const somaCoeficientes =
    numero(k1) +
    numero(k2) +
    numero(item.k3) +
    numero(item.k4);
  const tensaoCorrigida = temSinalAusencia(tensaoInicial, k1, k2, item.k3, item.k4)
    ? "-"
    : tensaoInicial && temTodosCoeficientes
      ? tensaoInicial * (1 + somaCoeficientes)
      : 0;
  const volumeCm3 =
    diametro && altura ? Math.PI * Math.pow(diametro / 20, 2) * (altura / 10) : 0;
  const massaEspecifica = temSinalAusencia(item.massa, item.diametro, item.altura)
    ? "-"
    : massa && volumeCm3
      ? massa / volumeCm3
      : 0;

  return {
    ...item,
    forcaRupturaN: forcaRupturaN === "-" ? "-" : forcaRupturaN ? arredondar(forcaRupturaN, 0) : "",
    tensaoInicial: tensaoInicial === "-" ? "-" : tensaoInicial ? arredondar(tensaoInicial, 2) : "",
    k1,
    k2,
    tensaoCorrigida: tensaoCorrigida === "-" ? "-" : tensaoCorrigida ? arredondar(tensaoCorrigida, 1) : "",
    massaEspecifica: massaEspecifica === "-" ? "-" : massaEspecifica ? arredondar(massaEspecifica, 3) : "",
  };
}

function valorVisual(valor, casas = null) {
  if (valor === "" || valor === null || valor === undefined) return "-";
  const n = numero(valor);
  if (!Number.isFinite(n)) return "-";
  return casas === null
    ? String(valor).replace(".", ",")
    : n.toLocaleString("pt-BR", {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas,
      });
}

function fotosComMinimo(extracao, quantidadeTestemunhos) {
  const fotos = Array.isArray(extracao?.relatorioFotografico)
    ? extracao.relatorioFotografico
    : [];
  const quantidadeMinima = Math.max(0, Number(quantidadeTestemunhos || 0));
  const total = Math.max(quantidadeMinima, fotos.length);

  return Array.from({ length: total }, (_, index) => ({
    id: fotos[index]?.id || `foto-${index + 1}`,
    legenda: fotos[index]?.legenda || `FOTO ${String(index + 1).padStart(2, "0")}`,
    url: fotos[index]?.url || "",
    path: fotos[index]?.path || "",
    nomeArquivo: fotos[index]?.nomeArquivo || "",
  }));
}

function formatarPrensaRuptura(prensa) {
  if (!prensa) return "";
  const partes = [
    prensa.codigo,
    prensa.empresa,
    prensa.equipamento,
    prensa.serie && `Serie ${prensa.serie}`,
    prensa.dataCalibracao && `Calibracao ${formatarDataBR(prensa.dataCalibracao)}`,
  ].filter(Boolean);
  return partes.join(" - ");
}

export default function DetalhesExtracao() {
  const { extracaoId } = useParams();
  const history = useHistory();
  const [extracao, setExtracao] = useState(null);
  const [testemunhos, setTestemunhos] = useState([]);
  const [calibracoesPrensa, setCalibracoesPrensa] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [modoPrint, setModoPrint] = useState(false);
  const [modoTabela, setModoTabela] = useState("total");
  const [temAlteracoes, setTemAlteracoes] = useState(false);
  const [openConfirmarCancelamento, setOpenConfirmarCancelamento] = useState(false);
  const [openFotos, setOpenFotos] = useState(false);
  const [uploadingFotoIndex, setUploadingFotoIndex] = useState(null);
  const [fotosDraft, setFotosDraft] = useState([]);
  const [navegacaoPendente, setNavegacaoPendente] = useState(null);
  const desbloquearNavegacaoRef = useRef(null);
  const extracaoOriginalRef = useRef(null);

  useEffect(() => {
    carregarExtracao();
    carregarCalibracoesPrensa();
  }, [extracaoId]);

  useEffect(() => {
    function confirmarSaida(event) {
      if (!temAlteracoes) return;

      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", confirmarSaida);
    return () => window.removeEventListener("beforeunload", confirmarSaida);
  }, [temAlteracoes]);

  useEffect(() => {
    if (!temAlteracoes) return undefined;

    desbloquearNavegacaoRef.current = history.block((location) => {
      setNavegacaoPendente(location);
      setOpenConfirmarCancelamento(true);
      return false;
    });

    return () => {
      desbloquearNavegacaoRef.current?.();
      desbloquearNavegacaoRef.current = null;
    };
  }, [history, temAlteracoes]);

  async function carregarCalibracoesPrensa() {
    const q = query(collection(db, "tblCalibracaoPrensas"), orderBy("dataCalibracao", "desc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setCalibracoesPrensa(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }

  async function carregarExtracao() {
    const snap = await getDoc(doc(db, "tblExtracoesTestemunhos", extracaoId));
    if (!snap.exists()) return;

    const dados = { id: snap.id, ...snap.data() };
    const quantidade = Number(dados.quantidadeTestemunhos || dados.testemunhos?.length || 0);
    const lista = Array.isArray(dados.testemunhos) ? [...dados.testemunhos] : [];

    while (lista.length < quantidade) {
      lista.push(novoTestemunho(lista.length, dados.dataExtracao));
    }

    const listaCalculada = lista.map(calcularTensoesDoTestemunho);

    const extracaoCarregada = { ...dados, testemunhos: listaCalculada };
    setExtracao(extracaoCarregada);
    extracaoOriginalRef.current = extracaoCarregada;
    setTestemunhos(listaCalculada);
    setTemAlteracoes(false);
    setOpenConfirmarCancelamento(false);
  }

  function atualizarTestemunho(index, campo, valor) {
    setMensagem("");
    setTemAlteracoes(true);

    if (
      campo === "dataExtracao" &&
      extracao?.dataExtracao &&
      valor !== extracao.dataExtracao
    ) {
      setExtracao((prev) => ({ ...prev, dataExtracao: "" }));
    }

    setTestemunhos((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? calcularTensoesDoTestemunho({
              ...item,
              [campo]: valor,
            })
          : item
      )
    );
  }

  function atualizarCampoExtracao(campo, valor) {
    setMensagem("");
    setTemAlteracoes(true);
    setExtracao((prev) => ({ ...prev, [campo]: valor }));
  }

  function visualizarRelatorio() {
    const temCargaRuptura = testemunhos.some(
      (testemunho) => String(testemunho.cargaRupturaTf ?? "").trim() !== ""
    );

    if (temCargaRuptura && !String(extracao?.horasAposExtracao || "").trim()) {
      setMensagem("É necessário informar a quantidade de horas após a extração em que o CP foi rompido.");
      return;
    }

    setModoPrint(true);
  }

  function handleEnterColuna(event, campo, index) {
    if (event.key !== "Enter") return;

    event.preventDefault();

    const proximo = document.querySelector(
      `[data-extracao-campo='${campo}'][data-extracao-index='${index + 1}']`
    );

    if (proximo) proximo.focus();
  }

  function adicionarTestemunho() {
    setMensagem("");
    setTemAlteracoes(true);
    setTestemunhos((prev) => [...prev, novoTestemunho(prev.length, extracao?.dataExtracao)]);
  }

  function removerTestemunho(index) {
    setMensagem("");
    setTemAlteracoes(true);
    setTestemunhos((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  }

  function abrirCadastroFotos() {
    const fotos = fotosComMinimo(extracao, testemunhos.length).map((foto) => ({ ...foto }));
    setFotosDraft(fotos);
    setOpenFotos(true);
  }

  function adicionarFotoExtra() {
    setFotosDraft((prev) => [
      ...prev,
      {
        id: `foto-${Date.now()}`,
        legenda: `FOTO ${String(prev.length + 1).padStart(2, "0")}`,
        url: "",
        path: "",
        nomeArquivo: "",
      },
    ]);
  }

  function selecionarImagemFoto(index, arquivo) {
    if (!arquivo) return;

    const previewUrl = URL.createObjectURL(arquivo);
    setFotosDraft((prev) =>
      prev.map((foto, itemIndex) =>
        itemIndex === index
          ? {
              ...foto,
              arquivo,
              previewUrl,
              nomeArquivo: arquivo.name,
            }
          : foto
      )
    );
  }

  function atualizarLegendaFoto(index, legenda) {
    setFotosDraft((prev) =>
      prev.map((foto, itemIndex) => (itemIndex === index ? { ...foto, legenda } : foto))
    );
  }

  function removerImagemFoto(index) {
    setFotosDraft((prev) =>
      prev.map((foto, itemIndex) =>
        itemIndex === index
          ? {
              ...foto,
              url: "",
              path: "",
              arquivo: null,
              previewUrl: "",
              nomeArquivo: "",
              removerPath: foto.path || foto.removerPath || "",
            }
          : foto
      )
    );
  }

  function cancelarFotos() {
    setFotosDraft([]);
    setOpenFotos(false);
  }

  async function salvarFotos() {
    if (!extracao?.id) return;

    setUploadingFotoIndex(-1);
    try {
      const fotosSalvas = [];

      for (let index = 0; index < fotosDraft.length; index++) {
        const foto = fotosDraft[index];
        let fotoFinal = {
          id: foto.id || `foto-${index + 1}`,
          legenda: foto.legenda || `FOTO ${String(index + 1).padStart(2, "0")}`,
          url: foto.url || "",
          path: foto.path || "",
          nomeArquivo: foto.nomeArquivo || "",
        };

        if (foto.arquivo) {
          const arquivoComprimido = await compressImage(foto.arquivo, {
            maxWidth: 1400,
            maxHeight: 1400,
            quality: 0.72,
          });
          const caminho = `extracoes/${extracao.id}/relatorio-fotografico/foto-${index + 1}-${Date.now()}.jpg`;
          const storageRef = ref(storage, caminho);
          await uploadBytes(storageRef, arquivoComprimido, { contentType: "image/jpeg" });
          const url = await getDownloadURL(storageRef);

          if (foto.path) {
            deleteObject(ref(storage, foto.path)).catch(() => {});
          }

          fotoFinal = {
            ...fotoFinal,
            url,
            path: caminho,
            nomeArquivo: foto.arquivo.name,
          };
        } else if (foto.removerPath) {
          deleteObject(ref(storage, foto.removerPath)).catch(() => {});
        }

        fotosSalvas.push(fotoFinal);
      }

      await updateDoc(doc(db, "tblExtracoesTestemunhos", extracao.id), {
        relatorioFotografico: fotosSalvas,
        atualizadoEm: new Date(),
      });

      setExtracao((prev) => {
        const atualizado = { ...prev, relatorioFotografico: fotosSalvas };
        extracaoOriginalRef.current = atualizado;
        return atualizado;
      });
      setFotosDraft([]);
      setOpenFotos(false);
      setMensagem("Relatório fotográfico salvo com sucesso!");
    } finally {
      setUploadingFotoIndex(null);
    }
  }

  async function salvarTudo() {
    setMensagem("");

    const temCargaRuptura = testemunhos.some(
      (testemunho) => String(testemunho.cargaRupturaTf ?? "").trim() !== ""
    );

    if (
      temCargaRuptura &&
      !extracao?.prensaExternaSemCertificado &&
      !extracao?.prensaRuptura?.id
    ) {
      setMensagem("É necessário selecionar a prensa utilizada para ruptura antes de salvar.");
      return;
    }

    if (
      extracao?.prensaExternaSemCertificado &&
      !String(extracao?.identificacaoPrensaExterna || "").trim()
    ) {
      setMensagem("É necessário informar a prensa utilizada no ensaio externo.");
      return;
    }

    setSalvando(true);

    try {
      const listaCalculada = testemunhos.map(calcularTensoesDoTestemunho);

      const extracaoParaSalvar = {
        ...extracao,
        testemunhos: listaCalculada,
        quantidadeTestemunhos: listaCalculada.length,
        relatorioFotografico: fotosComMinimo(extracao, listaCalculada.length),
        horasAposExtracao: extracao.horasAposExtracao || "",
        prensaRuptura: extracao.prensaRuptura || null,
        prensaExternaSemCertificado: Boolean(extracao.prensaExternaSemCertificado),
        identificacaoPrensaExterna: extracao.prensaExternaSemCertificado
          ? String(extracao.identificacaoPrensaExterna || "").trim()
          : "",
      };
      const dadosSalvos = await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, "tblExtracoesTestemunhos", extracaoId),
        original: extracaoOriginalRef.current,
        atual: extracaoParaSalvar,
        preparar: (dadosMesclados) => {
          const { id: _id, ...dados } = dadosMesclados;
          return dados;
        },
        metadados: { atualizadoEm: new Date() },
      });

      const extracaoSalva = { id: extracaoId, ...dadosSalvos };
      setTestemunhos(extracaoSalva.testemunhos || listaCalculada);
      setExtracao(extracaoSalva);
      extracaoOriginalRef.current = extracaoSalva;
      setTemAlteracoes(false);
      setMensagem("Salvo com sucesso!");
    } catch (error) {
      console.error(error);
      setMensagem("Erro ao salvar dados.");
    } finally {
      setSalvando(false);
    }
  }

  function solicitarCancelarAlteracoes() {
    setNavegacaoPendente(null);
    setOpenConfirmarCancelamento(true);
  }

  async function confirmarCancelarAlteracoes() {
    setOpenConfirmarCancelamento(false);
    await carregarExtracao();

    if (navegacaoPendente) {
      const destino = `${navegacaoPendente.pathname}${navegacaoPendente.search || ""}${navegacaoPendente.hash || ""}`;
      desbloquearNavegacaoRef.current?.();
      desbloquearNavegacaoRef.current = null;
      setNavegacaoPendente(null);
      history.push(destino);
    }
  }

  if (!extracao) return null;

  const fotosRelatorio = fotosComMinimo(extracao, testemunhos.length);

  if (modoPrint) {
    return (
      <RelatorioExtracoesPrint
        extracao={{ ...extracao, testemunhos }}
        fotosRelatorio={fotosRelatorio.filter((foto) => foto.url)}
        onVoltar={() => setModoPrint(false)}
      />
    );
  }

  return (
    <>
      <Navbar />

      <Box className="extracao-actions">
        <Box className="extracao-actions-titulos">
          <Typography component="h1">Ensaio de Extração de Testemunhos de Concreto</Typography>
          <Typography component="p">
            {extracao.codigo || "CE-RE-ET"}&nbsp;&nbsp; Rev.00 - {formatarDataBR(extracao.dataRelatorio || extracao.dataExtracao)}
          </Typography>
        </Box>

        <Box className="extracao-actions-botoes">
          {temAlteracoes ? (
            <>
              <Tooltip title="Visualizar relatÃ³rio">
                <span>
                  <IconButton color="primary" disabled aria-label="Visualizar relatÃ³rio">
                    <VisibilityIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="RelatÃ³rio fotogrÃ¡fico">
                <span>
                  <IconButton color="primary" disabled aria-label="RelatÃ³rio fotogrÃ¡fico">
                    <PhotoLibraryIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Imprimir relatÃ³rio">
                <span>
                  <IconButton color="primary" disabled aria-label="Imprimir relatÃ³rio">
                    <PrintIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button variant="outlined" disabled startIcon={<ArrowBackIcon />}>
                Voltar
              </Button>
              <Button variant="contained" onClick={salvarTudo} disabled={salvando} startIcon={<SaveIcon />}>
                {salvando ? "Salvando..." : "Salvar"}
              </Button>
              <Button data-confirmacao-cancelamento="propria" variant="outlined" onClick={solicitarCancelarAlteracoes} disabled={salvando}>
                Cancelar alterações
              </Button>
            </>
          ) : (
            <>
          <Tooltip title="Visualizar relatório">
            <IconButton color="primary" onClick={visualizarRelatorio} aria-label="Visualizar relatório">
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Relatório fotográfico">
            <IconButton color="primary" onClick={abrirCadastroFotos} aria-label="Relatório fotográfico">
              <PhotoLibraryIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Imprimir relatório">
            <IconButton color="primary" onClick={visualizarRelatorio} aria-label="Imprimir relatório">
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" onClick={() => history.goBack()} disabled={salvando} startIcon={<ArrowBackIcon />}>
            Voltar
          </Button>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "none" }}>
        <Typography variant="h6" fontWeight={700}>
          Ensaio de Extração de Testemunhos de Concreto
        </Typography>
        <Typography variant="body2">RQ 2.2.14 &nbsp;&nbsp; Rev.00 - 20/08/2025</Typography>
      </Box>

      <Box className="extracao-page" component="form" autoComplete="off">
        {mensagem && <Typography className="extracao-mensagem">{mensagem}</Typography>}

        <Dialog
          open={openFotos}
          onClose={(event, reason) => {
            if (uploadingFotoIndex !== null || reason === "backdropClick") return;
            setOpenFotos(false);
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Relatório fotográfico</DialogTitle>
          <DialogContent dividers>
            <Typography className="fotos-dialog-subtitulo">
              {testemunhos.length} testemunho(s) cadastrado(s). Cadastre uma foto por testemunho e adicione extras quando necessário.
            </Typography>

            <Box className="fotos-extracao-grid">
              {fotosDraft.map((foto, index) => (
                <Paper className="foto-extracao-card" key={foto.id || index} variant="outlined">
                  <Box className="foto-extracao-preview">
                    {foto.previewUrl || foto.url ? (
                      <img src={foto.previewUrl || foto.url} alt={foto.legenda || `Foto ${index + 1}`} />
                    ) : (
                      <AddPhotoAlternateIcon />
                    )}
                  </Box>

                  <TextField
                    size="small"
                    label="Legenda"
                    value={foto.legenda}
                    onChange={(e) => atualizarLegendaFoto(index, e.target.value)}
                    fullWidth
                  />

                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Button component="label" size="small" variant="contained" disabled={uploadingFotoIndex !== null}>
                      {foto.arquivo ? "Selecionada" : "Imagem"}
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={(e) => selecionarImagemFoto(index, e.target.files?.[0])}
                      />
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removerImagemFoto(index)}
                      disabled={(!foto.url && !foto.previewUrl) || uploadingFotoIndex !== null}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={adicionarFotoExtra} startIcon={<AddPhotoAlternateIcon />} disabled={uploadingFotoIndex !== null}>
              Acrescentar imagem
            </Button>
            <Button onClick={cancelarFotos} disabled={uploadingFotoIndex !== null}>
              Cancelar
            </Button>
            <Button onClick={salvarFotos} variant="contained" disabled={uploadingFotoIndex !== null}>
              {uploadingFotoIndex !== null ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openConfirmarCancelamento}
          onClose={() => {
            setNavegacaoPendente(null);
            setOpenConfirmarCancelamento(false);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Cancelar alterações</DialogTitle>
          <DialogContent dividers>
            <Typography>
              Ao confirmar você perderá todas as alterações. Deseja continuar?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setNavegacaoPendente(null);
              setOpenConfirmarCancelamento(false);
            }}>
              Não
            </Button>
            <Button color="error" variant="contained" onClick={confirmarCancelarAlteracoes}>
              Sim, cancelar
            </Button>
          </DialogActions>
        </Dialog>

        <Stack sx={{ display: "none" }} direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          <Button variant="contained" color="secondary" onClick={() => history.goBack()}>
            Voltar
          </Button>
          <Button variant="contained" color="success" onClick={salvarTudo} disabled={salvando}>
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
          <Button variant="contained" onClick={visualizarRelatorio}>
            Imprimir
          </Button>
          {mensagem && (
            <Typography sx={{ display: "flex", alignItems: "center" }}>{mensagem}</Typography>
          )}
        </Stack>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={700} mb={2}>
            DADOS DO ENSAIO
          </Typography>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }}
            gap={2}
          >
            <TextField
              label="Data Cadastro"
              value={formatarDataBR(extracao.dataRelatorio)}
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Cliente"
              value={extracao.cliente || "-"}
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Empreendimento/Obra"
              value={extracao.obra || "-"}
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Cód."
              value={extracao.codigo || "-"}
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Data Extração"
              value={formatarDataBR(extracao.dataExtracao)}
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Local"
              value={extracao.local || "-"}
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="FCK Projeto"
              value={extracao.fckProjeto ? `${extracao.fckProjeto} MPa` : "-"}
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Testemunhos"
              value={testemunhos.length}
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              select
              label="Prensa utilizada para ruptura"
              value={extracao.prensaRuptura?.id || ""}
              size="small"
              disabled={Boolean(extracao.prensaExternaSemCertificado)}
              onChange={(e) => {
                const prensa = calibracoesPrensa.find((item) => item.id === e.target.value);
                atualizarCampoExtracao("prensaRuptura", prensa ? { id: prensa.id, ...prensa } : null);
              }}
              sx={{ gridColumn: { xs: "auto", md: "span 2" } }}
            >
              <MenuItem value="">Selecione a prensa/certificado</MenuItem>
              {calibracoesPrensa.map((prensa) => (
                <MenuItem key={prensa.id} value={prensa.id}>
                  {formatarPrensaRuptura(prensa)}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(extracao.prensaExternaSemCertificado)}
                  onChange={(e) => {
                    const marcado = e.target.checked;
                    setMensagem("");
                    setTemAlteracoes(true);
                    setExtracao((prev) => ({
                      ...prev,
                      prensaExternaSemCertificado: marcado,
                      prensaRuptura: marcado ? null : prev.prensaRuptura,
                      identificacaoPrensaExterna: marcado
                        ? prev.identificacaoPrensaExterna || ""
                        : "",
                    }));
                  }}
                />
              }
              label="Ruptura realizada em laboratório externo, sem certificado da prensa"
              sx={{ gridColumn: { xs: "auto", md: "span 2" } }}
            />
            {extracao.prensaExternaSemCertificado && (
              <TextField
                label="Prensa utilizada no ensaio externo"
                value={extracao.identificacaoPrensaExterna || ""}
                size="small"
                required
                onChange={(e) =>
                  atualizarCampoExtracao("identificacaoPrensaExterna", e.target.value)
                }
                sx={{ gridColumn: { xs: "auto", md: "span 2" } }}
              />
            )}
            <TextField
              label="Horas após extração até a ruptura"
              value={extracao.horasAposExtracao || ""}
              size="small"
              onChange={(e) => atualizarCampoExtracao("horasAposExtracao", e.target.value)}
              inputProps={{ inputMode: "decimal" }}
            />
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            mb={2}
          >
            <Typography fontWeight={700}>ANÁLISE DOS TESTEMUNHOS</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                size="small"
                label="Visualização"
                value={modoTabela}
                onChange={(e) => setModoTabela(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="total">Total</MenuItem>
                <MenuItem value="resumido">Resumido</MenuItem>
              </TextField>
              <Button startIcon={<AddIcon />} variant="outlined" onClick={adicionarTestemunho}>
                Adicionar
              </Button>
            </Stack>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 1.5,
              backgroundColor: "#f8fafc",
              borderColor: "#cbd5e1",
            }}
          >
            <Typography fontWeight={800} fontSize={13} color="#0f2f5f">
              Posição/Face:
            </Typography>
            <Typography fontSize={12.5} color="#334155">
              Informe a posição do testemunho conforme a face de extração/ruptura adotada no ensaio.
              Exemplos usuais: topo, base, lateral, face interna, face externa ou outra referência clara
              que identifique de onde o CP foi extraído.
            </Typography>
          </Paper>

          <Box
            sx={{
              maxHeight: { xs: "calc(100dvh - 300px)", md: "calc(100dvh - 250px)" },
              overflowX: "scroll",
              overflowY: "auto",
              scrollbarGutter: "stable both-edges",
            }}
          >
            {false ? (
              <Table size="small" sx={{ minWidth: 1120 }}>
                <TableHead sx={{ backgroundColor: "#d9d9d9" }}>
                  <TableRow>
                    <TableCell sx={cellCenterSx} rowSpan={2}>Nº CP</TableCell>
                    <TableCell sx={cellCenterSx} colSpan={2}>Dimensão dos CP's</TableCell>
                    <TableCell sx={cellCenterSx} rowSpan={2}>Carga de<br />Ruptura<br />(Tf)</TableCell>
                    <TableCell sx={cellCenterSx} rowSpan={2}>Força de<br />Ruptura<br />(N)</TableCell>
                    <TableCell sx={cellCenterSx} rowSpan={2}>Tensão<br />fci,ext,<br />inicial<br />(MPa)</TableCell>
                    <TableCell sx={cellCenterSx} colSpan={4}>Coefic. de correção</TableCell>
                    <TableCell sx={cellCenterSx} rowSpan={2}>Tensão<br />fci,ext<br />(MPa)</TableCell>
                    <TableCell sx={cellCenterSx} rowSpan={2}>Massa<br />Específica<br />(g/m³)</TableCell>
                    <TableCell sx={cellCenterSx} rowSpan={2}>Frente de<br />carbonatação</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={cellCenterSx}>Diâm.<br />(mm)</TableCell>
                    <TableCell sx={cellCenterSx}>Altura<br />(mm)</TableCell>
                    <TableCell sx={cellCenterSx}><CabecalhoCoeficiente nome="K1" /></TableCell>
                    <TableCell sx={cellCenterSx}><CabecalhoCoeficiente nome="K2" /></TableCell>
                    <TableCell sx={cellCenterSx}><CabecalhoCoeficiente nome="K3" /></TableCell>
                    <TableCell sx={cellCenterSx}><CabecalhoCoeficiente nome="K4" /></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {testemunhos.map((item, index) => (
                    <TableRow key={index}>
                      {[
                        ["peca", "Nº CP"],
                        ["diametro", "Diâm."],
                        ["altura", "Altura"],
                        ["cargaRupturaTf", "Carga"],
                      ].map(([campo, label]) => (
                        <TableCell
                          key={campo}
                          sx={{
                            ...inputCellSx,
                            minWidth:
                              campo === "diametro" || campo === "altura"
                                ? 95
                                : campo === "cargaRupturaTf"
                                ? 105
                                : 75,
                          }}
                        >
                          <TextField
                            size="small"
                            placeholder={label}
                            value={item[campo] || ""}
                            onChange={(e) => atualizarTestemunho(index, campo, e.target.value)}
                            onKeyDown={(e) => handleEnterColuna(e, campo, index)}
                            sx={inputTabelaSx}
                            inputProps={{
                              "data-extracao-campo": campo,
                              "data-extracao-index": index,
                            }}
                          />
                        </TableCell>
                      ))}
                      <TableCell sx={{ ...cellCenterSx, minWidth: 105 }}>
                        <Typography fontWeight={600}>{valorVisual(item.forcaRupturaN, 0)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...cellCenterSx, minWidth: 85 }}>
                        <Typography fontWeight={600}>{valorVisual(item.tensaoInicial, 1)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...cellCenterSx, minWidth: 90 }}>
                        <Typography fontWeight={600}>{valorVisual(item.k1, 3)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...cellCenterSx, minWidth: 90 }}>
                        <Typography fontWeight={600}>{valorVisual(item.k2, 2)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...inputCellSx, minWidth: 90 }}>
                        <TextField
                          size="small"
                          placeholder="K3"
                          value={item.k3 || ""}
                          onChange={(e) => atualizarTestemunho(index, "k3", e.target.value)}
                          onKeyDown={(e) => handleEnterColuna(e, "k3", index)}
                          sx={inputTabelaSx}
                          inputProps={{
                            "data-extracao-campo": "k3",
                            "data-extracao-index": index,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...inputCellSx, minWidth: 90 }}>
                        <TextField
                          size="small"
                          placeholder="K4"
                          value={item.k4 || ""}
                          onChange={(e) => atualizarTestemunho(index, "k4", e.target.value)}
                          onKeyDown={(e) => handleEnterColuna(e, "k4", index)}
                          sx={inputTabelaSx}
                          inputProps={{
                            "data-extracao-campo": "k4",
                            "data-extracao-index": index,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...cellCenterSx, minWidth: 85 }}>
                        <Typography fontWeight={600} sx={{ color: "#000" }}>{valorVisual(item.tensaoCorrigida, 1)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...cellCenterSx, minWidth: 120 }}>
                        <Typography fontWeight={600}>{valorVisual(item.massaEspecifica, 3)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...inputCellSx, minWidth: 130 }}>
                        <TextField
                          size="small"
                          select
                          value={OPCOES_CARBONATACAO.includes(item.frenteCarbonatacao) ? item.frenteCarbonatacao : "-"}
                          onChange={(e) =>
                            atualizarTestemunho(index, "frenteCarbonatacao", e.target.value)
                          }
                          onKeyDown={(e) => handleEnterColuna(e, "frenteCarbonatacao", index)}
                          placeholder="Carbonatação"
                          sx={{
                            ...inputTabelaSx,
                            "& .MuiSelect-select": {
                              color: corCarbonatacao(OPCOES_CARBONATACAO.includes(item.frenteCarbonatacao) ? item.frenteCarbonatacao : "-"),
                              fontWeight: 700,
                            },
                          }}
                          inputProps={{
                            "data-extracao-campo": "frenteCarbonatacao",
                            "data-extracao-index": index,
                          }}
                        >
                          {OPCOES_CARBONATACAO.map((opcao) => (
                            <MenuItem key={opcao} value={opcao} sx={{ color: corCarbonatacao(opcao), fontWeight: 700 }}>
                              {opcao}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
            <Table
              stickyHeader
              size="small"
              sx={{ minWidth: modoTabela === "resumido" ? 1120 : 1480 }}
            >
              <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                <TableRow>
                  <TableCell sx={cellCenterSx}>Nº CP</TableCell>
                  {modoTabela !== "resumido" && <TableCell sx={cellCenterSx}>Identificação</TableCell>}
                  {modoTabela !== "resumido" && <TableCell sx={cellCenterSx}>Data extração</TableCell>}
                  {modoTabela !== "resumido" && <TableCell sx={cellCenterSx}>Posição/Face</TableCell>}
                  <TableCell sx={cellCenterSx}>Diâm. (mm)</TableCell>
                  <TableCell sx={cellCenterSx}>Altura (mm)</TableCell>
                  <TableCell sx={cellCenterSx}>Carga (Tf)</TableCell>
                  <TableCell sx={cellCenterSx}>Força ruptura (N)</TableCell>
                  {modoTabela !== "resumido" && <TableCell sx={cellCenterSx}>Massa (g)</TableCell>}
                  <TableCell sx={cellCenterSx}><CabecalhoCoeficiente nome="K1" /></TableCell>
                  <TableCell sx={cellCenterSx}><CabecalhoCoeficiente nome="K2" /></TableCell>
                  <TableCell sx={cellCenterSx}><CabecalhoCoeficiente nome="K3" /></TableCell>
                  <TableCell sx={cellCenterSx}><CabecalhoCoeficiente nome="K4" /></TableCell>
                  <TableCell sx={cellCenterSx}>Tensão inicial</TableCell>
                  <TableCell sx={cellCenterSx}>Tensão corrigida</TableCell>
                  <TableCell sx={cellCenterSx}>Massa específica</TableCell>
                  <TableCell sx={cellCenterSx}>Carbonatação</TableCell>
                  {modoTabela !== "resumido" && <TableCell sx={cellCenterSx}>Observação</TableCell>}
                  {modoTabela !== "resumido" && <TableCell sx={cellCenterSx}>Ações</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {testemunhos.map((item, index) => (
                  <TableRow key={index}>
                    {[
                      ["peca", "Nº CP"],
                      ["identificacao", "Identificação"],
                      ["dataExtracao", "Data"],
                      ["posicaoFace", "Posição"],
                      ["diametro", "Diâm."],
                      ["altura", "Altura"],
                      ["cargaRupturaTf", "Carga"],
                    ].filter(
                      ([campo]) =>
                        modoTabela !== "resumido" ||
                        !["identificacao", "dataExtracao", "posicaoFace"].includes(campo)
                    ).map(([campo, label]) => (
                      <TableCell
                        key={campo}
                        sx={{
                          ...inputCellSx,
                          minWidth:
                            campo === "identificacao"
                              ? 270
                              : campo === "diametro" || campo === "altura"
                              ? 95
                              : campo === "cargaRupturaTf"
                              ? 105
                              : 75,
                        }}
                      >
                        <TextField
                          size="small"
                          type={campo === "dataExtracao" ? "date" : "text"}
                          InputLabelProps={campo === "dataExtracao" ? { shrink: true } : undefined}
                          placeholder={label}
                          value={item[campo] || ""}
                          onChange={(e) => atualizarTestemunho(index, campo, e.target.value)}
                          onKeyDown={(e) => handleEnterColuna(e, campo, index)}
                          sx={inputTabelaSx}
                          inputProps={{
                            "data-extracao-campo": campo,
                            "data-extracao-index": index,
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...cellCenterSx, minWidth: 100 }}>
                      <Typography fontWeight={600}>
                        {valorVisual(item.forcaRupturaN, 0)}
                      </Typography>
                    </TableCell>
                    {modoTabela !== "resumido" && <TableCell sx={{ ...inputCellSx, minWidth: 85 }}>
                      <TextField
                        size="small"
                        placeholder="Massa"
                        value={item.massa || ""}
                        onChange={(e) => atualizarTestemunho(index, "massa", e.target.value)}
                        onKeyDown={(e) => handleEnterColuna(e, "massa", index)}
                        sx={inputTabelaSx}
                        inputProps={{
                          "data-extracao-campo": "massa",
                          "data-extracao-index": index,
                        }}
                      />
                    </TableCell>}
                    <TableCell sx={{ ...cellCenterSx, minWidth: 90 }}>
                      <Typography fontWeight={600}>{valorVisual(item.k1, 3)}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellCenterSx, minWidth: 90 }}>
                      <Typography fontWeight={600}>{valorVisual(item.k2, 2)}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...inputCellSx, minWidth: 90 }}>
                      <TextField
                        size="small"
                        placeholder="K3"
                        value={item.k3 || ""}
                        onChange={(e) => atualizarTestemunho(index, "k3", e.target.value)}
                        onKeyDown={(e) => handleEnterColuna(e, "k3", index)}
                        sx={inputTabelaSx}
                        inputProps={{
                          "data-extracao-campo": "k3",
                          "data-extracao-index": index,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...inputCellSx, minWidth: 90 }}>
                      <TextField
                        size="small"
                        placeholder="K4"
                        value={item.k4 || ""}
                        onChange={(e) => atualizarTestemunho(index, "k4", e.target.value)}
                        onKeyDown={(e) => handleEnterColuna(e, "k4", index)}
                        sx={inputTabelaSx}
                        inputProps={{
                          "data-extracao-campo": "k4",
                          "data-extracao-index": index,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellCenterSx, minWidth: 90 }}>
                      <Typography fontWeight={600}>
                        {valorVisual(item.tensaoInicial, 1)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellCenterSx, minWidth: 95 }}>
                      <Typography fontWeight={600} sx={{ color: "#000" }}>
                        {valorVisual(item.tensaoCorrigida, 1)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...inputCellSx, minWidth: 130 }}>
                      <Typography fontWeight={600}>
                        {valorVisual(item.massaEspecifica, 3)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...inputCellSx, minWidth: 130 }}>
                      <TextField
                        size="small"
                        select
                        value={OPCOES_CARBONATACAO.includes(item.frenteCarbonatacao) ? item.frenteCarbonatacao : "-"}
                        onChange={(e) =>
                          atualizarTestemunho(index, "frenteCarbonatacao", e.target.value)
                        }
                        onKeyDown={(e) => handleEnterColuna(e, "frenteCarbonatacao", index)}
                        placeholder="Carbonatação"
                        sx={{
                          ...inputTabelaSx,
                          "& .MuiSelect-select": {
                            color: corCarbonatacao(OPCOES_CARBONATACAO.includes(item.frenteCarbonatacao) ? item.frenteCarbonatacao : "-"),
                            fontWeight: 700,
                          },
                        }}
                        inputProps={{
                          "data-extracao-campo": "frenteCarbonatacao",
                          "data-extracao-index": index,
                        }}
                      >
                        {OPCOES_CARBONATACAO.map((opcao) => (
                          <MenuItem key={opcao} value={opcao} sx={{ color: corCarbonatacao(opcao), fontWeight: 700 }}>
                            {opcao}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    {modoTabela !== "resumido" && <TableCell sx={{ ...inputCellSx, minWidth: 290 }}>
                      <TextField
                        size="small"
                        value={item.observacao || ""}
                        onChange={(e) => atualizarTestemunho(index, "observacao", e.target.value)}
                        onKeyDown={(e) => handleEnterColuna(e, "observacao", index)}
                        placeholder="Observação"
                        sx={inputTabelaSx}
                        inputProps={{
                          "data-extracao-campo": "observacao",
                          "data-extracao-index": index,
                        }}
                      />
                    </TableCell>}
                    {modoTabela !== "resumido" && <TableCell align="center">
                      <IconButton size="small" onClick={() => removerTestemunho(index)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            )}
          </Box>
        </Paper>
      </Box>
    </>
  );
}
